import { yupResolver } from "@hookform/resolvers/yup";
import { AuthenticationError, useAppStatus } from "@wexinc/wex-common-web";
import { Button, FieldValidation, Form, TextField, UseFormReturn, WexLogo } from "@wexinc/wex-ui";
import { CopRoutes } from "app/routes";
import { useEffect, useRef, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate, useSearchParams } from "react-router-dom";
import useCopSecurityService, { SignInParams } from "services/security/useCopSecurityService";
import * as yup from "yup";
import BlobSVG from "./BlobSVG";
import mainImage from "./MainImage.png";

interface LoginSchema {
  username: string;
  password: string;
}

const schema = yup.object<LoginSchema>({
  username: yup.string().required("Email is required.").email("Must be a valid email.").trim(),
  password: yup.string().required("Password is required."),
});

const UsernamePasswordLogin: React.FC = () => {
  const [enableSignInButton, setEnableSignInButton] = useState(true);
  const securityService = useCopSecurityService();
  const originalUrl = securityService.getOriginalUrl();
  const redirectUrl = securityService.getRedirectUrl();
  const [authError, setAuthError] = useState<string>();
  const { showBoundary } = useErrorBoundary();
  const [searchParams] = useSearchParams();
  const appStatus = useAppStatus();
  const useFormReturn = useRef<UseFormReturn>();
  const navigate = useNavigate();

  const isFirefox = navigator.userAgent.indexOf("Firefox") != -1;
  // extra check b/c Chrome user agent includes the phrase "Safari"
  const isSafari = navigator.userAgent.indexOf("Safari") != -1 && navigator.userAgent.indexOf("Chrome") === -1;
  const isFirefoxOrSafari = isFirefox || isSafari;

  async function handleSubmit(loginParams: SignInParams) {
    setEnableSignInButton(false);

    try {
      await securityService.loginViaUsernamePassword(loginParams, originalUrl, redirectUrl);
    } catch (error) {
      setEnableSignInButton(true);
      if (error instanceof AuthenticationError) {
        setAuthError(error.message);
      } else {
        showBoundary(error);
      }
    }
  }

  function resetPasswordRequest() {
    const currentEmail = useFormReturn.current?.getValues().username || "";
    navigate(`${CopRoutes.passwordResetRequest}?email=${encodeURIComponent(currentEmail)}`);
  }

  function onFormInit(useFormReturnIncoming: UseFormReturn) {
    useFormReturn.current = useFormReturnIncoming;
  }

  // Annoying to have to do, but in the authorized area of the app, securityService takes care of this.
  useEffect(() => appStatus.setAppStatusAction("reset"), []);

  return (
    <>
      <div className="flex h-full items-center mx-auto">
        <div className="flex items-center mx-auto">
          <section className="flex flex-col w-[400px] ml-[20px]">
            <WexLogo
              height="40px"
              width="124px"
            />
            <div className="pb-10 pt-2 text-neutral-dark-60 text-lg-plus font-semibold">Customer Operations Portal</div>
            <div className="pb-4 text-neutral-dark-70 font-bold text-xl">Log in</div>
            {authError && (
              <FieldValidation
                className="text-md text-center pb-2"
                testId="authentication-error"
                error={authError}
              />
            )}
            <Form
              className="relative"
              onSubmit={handleSubmit}
              onInit={onFormInit}
              options={{
                resolver: yupResolver(schema),
                defaultValues: { username: searchParams.get("email") || "" },
              }}
            >
              <>
                <TextField
                  name="username"
                  label="Email"
                  containerClassName="mb-10"
                />
                <Button
                  buttonType="secondary-neutral"
                  testId="reset-password"
                  onClick={resetPasswordRequest}
                  size="small"
                  className="absolute right-0 bottom-[80px]"
                >
                  Reset password
                </Button>
                <TextField
                  name="password"
                  label="Password"
                  type="password"
                  containerClassName="!mb-16"
                />
                <Button
                  id="login-submit"
                  className="w-full !h-14 mt-10"
                  testId="login-button"
                  type="submit"
                  disabled={!enableSignInButton}
                >
                  Sign In
                </Button>
              </>
            </Form>
            {isFirefoxOrSafari && (
              <p className="mt-6 text-neutral-dark-60 text-sm">{`${isFirefox ? "Firefox" : ""}${
                isSafari ? "Safari" : ""
              } users must enable ${isFirefox ? "cross-site tracking cookies" : ""}${
                isSafari ? "cross-site tracking and cookies" : ""
              } in their browser preferences in order to proceed with login`}</p>
            )}
          </section>
          <section className="relative ml-[110px]">
            <img
              src={mainImage}
              alt="Illustration"
              className="relative z-10"
            />
          </section>
        </div>
      </div>

      <BlobSVG
        width="596"
        height="297"
        viewBox="0 0 596 297"
        className="absolute bottom-0 right-0"
        opacity="0.5"
      />

      <BlobSVG
        width="565"
        height="285"
        viewBox="0 0 565 285"
        className="absolute bottom-0 right-0"
        opacity="1"
      />
    </>
  );
};

export default UsernamePasswordLogin;
